<template>
  <div
    class="
      form-row
      align-items-start align-items-md-center
      flex-column flex-md-row
    "
  >
    <div class="col col-md-auto">
      <label class="sr-only text-white" for="position">
        {{ localizedContent.chooseYourPosition }}
      </label>
      <select
        id="position"
        v-model="positionId"
        name="position"
        class="custom-select custom-select-sm font-main custom-dd"
        :disabled="areInteractionsDisabled"
      >
        <option :value="0" :disabled="!positions.length">
          {{ localizedContent.choosePosition }}
        </option>
        <option
          v-for="position in positions"
          :key="position.id"
          :value="position.id"
        >
          {{ position.title }}
        </option>
      </select>
    </div>
    <div class="col col-md-auto">
      <label class="sr-only text-white" for="vehicleType">
        {{ localizedContent.vehicleType }}
      </label>
      <select
        id="vehicleType"
        v-model="vehicleTypeId"
        name="vehicleType"
        class="custom-select custom-select-sm font-main custom-dd mt-2 mt-md-0"
        :class="{ hidden: !positionId }"
        :disabled="isFilterDisabled"
        @change="onFilterChange"
      >
        <option :value="0">
          {{ localizedContent.vehicleType }}
        </option>
        <option
          v-for="vehicleType in vehicleTypes"
          :key="vehicleType.id"
          :value="vehicleType.id"
          :disabled="isOptionDisabled"
        >
          {{ vehicleType.title }}
        </option>
      </select>
    </div>
    <div class="col col-md-auto text-center">
      <span :class="{ show: positionId }" class="or">
        {{ localizedContent.or }}
      </span>
    </div>
    <div class="col col-md-auto">
      <label class="sr-only text-white" for="Vocation">
        {{ localizedContent.chooseYourVocation }}
      </label>
      <select
        id="vocation"
        v-model="vocationId"
        class="custom-select custom-select-sm font-main custom-dd mt-2 mt-md-0"
        :class="{ hidden: !positionId }"
        :disabled="isFilterDisabled"
        @change="onFilterChange"
      >
        <option :value="0">
          {{ localizedContent.chooseVocation }}
        </option>
        <option
          v-for="vocation in vocations"
          :key="vocation.id"
          :value="vocation.id"
          :disabled="isOptionDisabled"
        >
          {{ vocation.title }}
        </option>
      </select>
    </div>
    <div class="col-auto">
      <button
        class="btn qs-btn font-main mt-2 mt-lg-0"
        role="button"
        :disabled="isSubmitDisabled"
        @click="onSubmit"
      >
        {{ localizedContent.search }}
      </button>
    </div>
  </div>
</template>

<script>
import log from 'loglevel';

import { defaultHeaders } from '@/src/utilities/fetch-utilities';
import { createResult } from '@/src/utilities/result';

/**
 * @typedef LocalizedContent
 * @property {string} search
 * @property {string} chooseVocation
 * @property {string} chooseYourVocation
 * @property {string} vehicleType
 * @property {string} choosePosition
 * @property {string} chooseYourPosition
 * @property {string} or
 */

/**
 * @typedef Context
 * @property {string} culture
 * @property {string} productTaxonomyIdentifer
 */

/**
 * @typedef Links
 * @property {string} search
 */

/**
 * @typedef Taxonomy
 * @property {number} id
 * @property {string} identifier
 * @property {string} title
 */

/**
 * @typedef Data
 * @property {number} positionId
 * @property {number} vocationId
 * @property {number} vehicleTypeId
 * @property {Taxonomy[]} positions
 * @property {Taxonomy[]} vocations
 * @property {Taxonomy[]} vehicleTypes
 * @property {boolean} isLoading
 */

export default {
  name: 'QuickSearch',

  props: {
    /** @type {import('vue').PropOptions<LocalizedContent>} */
    localizedContent: {
      type: Object,
      required: true,
    },

    /** @type {import('vue').PropOptions<Context>} */
    context: {
      type: Object,
      required: true,
    },

    /** @type {import('vue').PropOptions<Links>} */
    links: {
      type: Object,
      required: true,
    },
  },

  /**
   * @returns {Data}
   */
  data() {
    return {
      positionId: 0,
      vocationId: 0,
      vehicleTypeId: 0,

      positions: [],
      vehicleTypes: [],
      vocations: [],

      isLoading: false,
    };
  },

  computed: {
    /**
     * @returns {boolean}
     */
    isSubmitDisabled() {
      return this.isFilterDisabled || this.isOptionDisabled;
    },

    /**
     * @returns {boolean}
     */
    isFilterDisabled() {
      return this.positionId === 0 || this.areInteractionsDisabled;
    },

    /**
     * @returns {boolean}
     */
    isOptionDisabled() {
      return !this.vehicleTypes.length || !this.vocations.length;
    },

    /**
     * @returns {boolean}
     */
    areInteractionsDisabled() {
      return this.isLoading;
    },
  },

  watch: {
    positionId: {
      handler(curr, _prev) {
        if (curr === '0' || curr === 0) {
          this.vocationId = 0;
          this.vehicleTypeId = 0;
        }

        this.onFilterChange();
      },
    },
  },

  async created() {
    await this.onFilterChange();
  },

  methods: {
    async onFilterChange() {
      this.isLoading = true;

      const params = new URLSearchParams({
        positionId: this.positionId.toString(),
        vocationId: this.vocationId.toString(),
        vehicleTypeId: this.vehicleTypeId.toString(),
      });

      const result = await createResult(
        fetch(`/api/quick-search/${this.context.culture}?${params}`, {
          method: 'GET',
          headers: defaultHeaders(),
        }).then((resp) => resp.json()),
      );

      this.isLoading = false;

      if (result.isFailure) {
        return;
      }

      this.positions = result.value.positions;
      this.vehicleTypes = result.value.vehicleTypes;
      this.vocations = result.value.vocations;

      if (!this.vehicleTypes.find((t) => t.id === this.vehicleTypeId)) {
        this.vehicleTypeId = 0;
      }

      if (!this.vocations.find((v) => v.id === this.vocationId)) {
        this.vocationId = 0;
      }
    },

    onSubmit() {
      const url = new URL(this.links.search, window.location.href);
      const queryKeyIdentifiers = 'ti';

      const position = this.positions.find((p) => p.id === this.positionId);

      if (!position) {
        log.error('Could not find position', this.positionId);

        return;
      }

      const identifiers = [
        this.context.productTaxonomyIdentifer,
        position.identifier,
      ];

      const vocation = this.vocations.find((v) => v.id === this.vocationId);

      if (vocation) {
        identifiers.push(vocation.identifier);
      }

      const vehicleType = this.vehicleTypes.find(
        (vt) => vt.id === this.vehicleTypeId,
      );

      if (vehicleType) {
        identifiers.push(vehicleType.identifier);
      }

      identifiers.forEach((i) =>
        url.searchParams.append(queryKeyIdentifiers, i),
      );
      window.location.href = url.toString();
    },
  },
};
</script>
